import { useEffect, useState, type ComponentProps } from "react";
import { Input } from "@groot/ui/input";

const toText = (v?: number): string => {
  if (v === undefined || v === null || Number.isNaN(v)) return "";
  return String(v);
};

export interface NumberInputProps extends Omit<
  ComponentProps<typeof Input>,
  "value" | "onChange" | "type"
> {
  value?: number;
  /** Receives `NaN` while the field is empty so the user can clear it. */
  onValueChange: (value: number) => void;
}

/**
 * Numeric input that keeps its own text so intermediate states ("", "1.")
 * survive re-renders, while reporting parsed numbers to the caller.
 */
export function NumberInput({ value, onValueChange, ...props }: NumberInputProps) {
  const [text, setText] = useState(() => toText(value));

  useEffect(() => {
    const parsed = text === "" ? NaN : Number(text);
    const bothEmpty =
      Number.isNaN(parsed) && (value === undefined || value === null || Number.isNaN(value));
    if (!bothEmpty && parsed !== value) {
      setText(toText(value));
    }
    // Only re-sync when the external value changes, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      type="number"
      inputMode="decimal"
      {...props}
      value={text}
      onChange={(e) => {
        const next = e.target.value;
        setText(next);
        onValueChange(next === "" ? NaN : Number(next));
      }}
    />
  );
}
